import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Slide,
  SxProps,
  Theme,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useAccessToken, useSubjectLanguage, useUserState } from '@features/user';
import APIService from '../../services/APIService';
import { useSnackbar } from 'notistack';

interface FeatureBannerProps {
  isVisible?: boolean;
  onDismiss?: () => void;
  displayStyle?: 'banner' | 'card';
}

const LOCAL_STORAGE_KEY = 'ehr_mode_banner_dismissed';
const HELP_CENTER_URL = 'https://jotpsych.frontkb.com/en';

export const FeatureBanner: React.FC<FeatureBannerProps> = ({
  isVisible: propIsVisible = true,
  onDismiss,
  displayStyle = 'card',
}) => {
  // Check sessionStorage to see if the banner has been dismissed before
  const [isDismissed, setIsDismissed] = useState<boolean>(
    sessionStorage.getItem(LOCAL_STORAGE_KEY) === 'true',
  );

  const navigate = useNavigate();
  const { getAccessToken } = useAccessToken();
  const subjectLanguage = useSubjectLanguage();
  const userState = useUserState();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  // Check if ehr_mode is already enabled
  const isEhrModeEnabled = userState?.featureFlags?.ehr_mode === true;

  // Determine if the banner should be visible
  const isVisible = propIsVisible && !isDismissed && !isEhrModeEnabled;

  if (!isVisible) return null;

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    if (onDismiss) onDismiss();
  };

  const handleOptIn = async () => {
    setIsLoading(true);

    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        enqueueSnackbar('Authentication error. Please log in again.', {
          variant: 'error',
          anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        });
        setIsLoading(false);
        return;
      }

      const response = await APIService.makeAPIPostRequest({
        requestString: '/v2/provider/features/toggle-ehr-mode',
        accessToken,
        body: { enabled: true },
      });

      if (response.ok) {
        enqueueSnackbar("You're now using the new recording screen!", {
          variant: 'success',
        });
        window.location.href = '/';
      } else {
        enqueueSnackbar('Failed to enable the new recording screen. Please try again later.', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.error('Error toggling EHR mode:', error);
      enqueueSnackbar('An error occurred. Please try again later.', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Styles based on displayStyle
  const getContainerStyles = (): SxProps<Theme> => {
    if (displayStyle === 'banner') {
      return {
        position: 'relative',
        width: '100%',
        zIndex: 1200,
        borderRadius: 0,
        backgroundColor: '#f0f8ff', // Light blue background
        border: '1px solid #d0e8ff',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      };
    }

    return {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      width: { xs: '95%', sm: '400px', md: '400px' },
      maxWidth: '400px',
      zIndex: 1300,
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: '#f0f8ff', // Light blue background
      border: '1px solid #d0e8ff',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    };
  };

  return (
    <Slide
      direction={displayStyle === 'banner' ? 'down' : 'up'}
      in={isVisible}
      mountOnEnter
      unmountOnExit
    >
      <Paper elevation={3} sx={getContainerStyles()}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              New Features!
            </Typography>
            <IconButton size="small" onClick={handleDismiss} aria-label="close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body1">
              JotPsych can now do so much more. If now is a good time, click the button below to
              enable new features like:
            </Typography>
            <Box sx={{ mt: 1, mb: 1, pl: 2 }}>
              <Typography component="div" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                • Automated appointment reminders and forms
              </Typography>
              <Typography component="div" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                • Multiple recordings per encounter
              </Typography>
              <Typography component="div" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                • AI-assisted scheduling and calendar
              </Typography>
              <Typography component="div" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                • Enhanced {subjectLanguage} profiles
              </Typography>
              <Typography component="div" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                • Send forms and questionnaires to {subjectLanguage}s
              </Typography>
            </Box>
            <Typography variant="body1">
              Try them now! You can switch back to your current features at any time in the user
              settings menu.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            {/* <Button
              variant="text"
              size="small"
              component={Link}
              href={HELP_CENTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: "none",
                color: "text.secondary",
                fontSize: "0.875rem",
              }}
            >
              Learn More
            </Button> */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleOptIn}
              disabled={isLoading}
              sx={{
                textTransform: 'none',
                borderRadius: '20px',
                px: 3,
              }}
            >
              {isLoading ? 'Updating...' : 'Enable New Features'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default FeatureBanner;
